import { WaterFactoryModelFactory } from "@/core/models/water-factory.model";
import { WaterInvoiceModel } from "@/core/models/water-invoice.model";
import { formatCurrencyPipe } from "@/core/pipes/format-currency.pipe";
import { formatDatePipe } from "@/core/pipes/format-date.pipe";
import { WaterFactoryRepo } from "@/core/repos/tables/water-factory.repo";
import { WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import container from "@/lib/di/container";
import { formatResellerUnitPrice } from '@/app/home/customer/contracts/components/reseller-discount-policy.helpers';
import { InvoiceSummaryRows } from "./InvoiceSummaryRows";
import { Alert, QRCode, Button, Modal } from "antd";
import { useState, useEffect, Dispatch, SetStateAction } from "react";

const waterInvoiceRepo = container.get(WaterInvoiceRepo);
const waterFactoryRepo = container.get(WaterFactoryRepo);

interface InvoiceTableProps {
    data: WaterInvoiceModel,
    qrData?: string,
    preview?: boolean
}

export const InvoiceTable: React.FC<InvoiceTableProps> = ({data, qrData, preview}) => {

    const [vnPayQR, setVnPayQR] = useState<string>();
    const [waterFactory, setWaterFactory] = useState(WaterFactoryModelFactory.getEmpty());

    const getWaterFactoryInfo = async () => {
        if (data.waterFactoryId) {
            const _waterFactory = await waterFactoryRepo.detail(data.waterFactoryId);
            if (_waterFactory) {
                setWaterFactory(_waterFactory);
            } else {
                setWaterFactory(WaterFactoryModelFactory.getEmpty());
            }
        }
    }

    useEffect(()=>{
        setVnPayQR(undefined);
        getWaterFactoryInfo();
    }, [data])

    const getVnPayQR = async () => {
        const _vnPayQR = await waterInvoiceRepo.getVNPayQR(data);
        setVnPayQR(_vnPayQR);
    }

    return (
        <table className="invoice-table text-lg w-full">
            <thead>
                <tr>
                <td className="font-bold">Họ và tên địa chỉ khách hàng</td>
                <td className="font-bold">Ngày đọc tháng này</td>
                <td className="font-bold">Ngày đọc tháng trước</td>
                <td className="font-bold">Số ngày sử dụng</td>
                <td className="font-bold">Ngày xuất HDĐT</td>
                <td className="font-bold">Số hóa đơn</td>
                </tr>
            </thead>
            <tbody>
                <tr>
                <td rowSpan={3 + data.waterInvoiceRows.length}>
                    {data.waterUser?.code}
                    <br />
                    {data.waterUser?.name}
                    <br />
                </td>
                <td>{formatDatePipe(data.readDate)}</td>
                <td>{formatDatePipe(data.readDateLast)}</td>
                <td>{data.useDay}</td>
                <td>{formatDatePipe(data.eInvoiceArisingDate)}</td>
                <td>{data.eInvoiceNo}</td>
                </tr>
                <tr>
                <td className="font-bold">Chỉ số mới</td>
                <td className="font-bold">Chỉ số cũ</td>
                <td className="font-bold">M3 tiêu thụ</td>
                <td className="font-bold" colSpan={2}>Số hộ dùng chung: 1</td>
                </tr>
                <tr>
                <td>{data.waterMeterNumber}</td>
                <td>{data.waterMeterNumberLast}</td>
                <td>{data.amount}</td>
                <td className="font-bold text-right">Đơn giá</td>
                <td className="font-bold text-right">Thành tiền</td>
                </tr>
                {data.waterInvoiceRows.map((row, index) => (
                    <tr key={index}>
                        <td colSpan={2}>{row.name}{data.resellerPricingMode && row.code && <div className="text-sm text-gray-600">Mã đối tượng giá: {row.code}</div>}{row.vatRate != null && <div className="text-sm text-gray-600">VAT {row.vatRate}%{row.environmentFeeRate != null ? ` · Phí BVMT ${row.environmentFeeRate}%` : ''}</div>}</td>
                        <td>{row.amount}</td>
                        <td className="text-right">{data.resellerPricingMode ? formatResellerUnitPrice(row.price) : formatCurrencyPipe(row.price)}</td>
                        <td className="text-right">{formatCurrencyPipe(row.total)}</td>
                    </tr>
                ))}

                <InvoiceSummaryRows data={data} sellerName={waterFactory.name} />
                <tr>
                <td colSpan={5}>Số tiền viết bằng chữ: {data.totalText}</td>
                <td></td>
                </tr>
                {!preview&&<tr>
                <td colSpan={3}>
                    <div className="px-4">
                        {qrData&&<QRCode value={qrData} size={250}></QRCode>}
                    </div>
                </td>
                <td colSpan={3}>
                    <div className="px-4 flex flex-row justify-end">
                        {vnPayQR
                            ?<img src={vnPayQR} height={250} width={250}></img>
                            :<Button type="default" onClick={getVnPayQR}>Tạo QR VNPay</Button>
                        }
                    </div>
                </td>
                </tr>}
            </tbody>
        </table>
    );
};

interface InvoiceModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: WaterInvoiceModel,
    qrData: string
}
export const InvoiceModal: React.FC<InvoiceModalProps> = ({isModalOpen, setIsModalOpen, data, qrData}) => {
    const handleCancel = () => {
        setIsModalOpen(false);
    };
    return <Modal open={isModalOpen} onCancel={handleCancel} width={1200} 
        style={{
            top: 20
        }}
        styles={{
            mask: {
                backdropFilter: 'blur(2px)',
                backgroundColor: 'var(--primary-mask)'
            }
        }}
        footer={[]}
    >
        <div className="flex flex-row w-full items-start justify-start select-none">
            <p className="ml-6 text-2xl font-bold text-primary">THÔNG TIN HOÁ ĐƠN</p>
        </div>
        {(data.waterMeterNumber ?? 0) < (data.waterMeterNumberLast ?? 0) && (
            <div className="px-6 pt-4">
                <Alert
                    type="info"
                    showIcon
                    message="Tháng này có thay đồng hồ giữa kỳ"
                    description="Chỉ số mới thấp hơn chỉ số cũ do đã lắp đồng hồ mới. Lượng nước tiêu thụ trong hoá đơn được tính gộp 2 đoạn (đoạn đồng hồ cũ + đoạn đồng hồ mới), không phải đồng hồ quay ngược."
                />
            </div>
        )}
        <div className="flex flex-col items-center">
            <div className="py-4 px-4 w-full">
                <InvoiceTable data={data} qrData={qrData}></InvoiceTable>
            </div>
        </div>
    </Modal>
} 
